import { useCallback, useState } from "react";

import { SearchBox } from "@mapbox/search-js-react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { isNil } from "lodash";

import { EVOTRUX_BLUE } from "constants/index";

import countries from "./countries";
import provinces from "./provinces";
import states from "./states";

import type { SearchBoxRetrieveResponse } from "@mapbox/search-js-core";

const TOKEN = import.meta.env.REACT_APP_MAPBOX_PUBLIC_KEY ?? "ERROR";

interface AddressFormState {
  address: string;
  city: string;
  province: string;
  postal: string;
  country: string;
}

interface Props {
  onValueChanged: (key: keyof CompanyAddress, value: string) => void;
  initialValues?: AddressFormState;
}

const EMPTY_ADDRESS_FORM_STATE: AddressFormState = {
  address: "",
  city: "",
  province: "",
  postal: "",
  country: "",
};

function isValidCountry(countryCode: string | undefined) {
  if (isNil(countryCode)) {
    return false;
  }

  return countries.some((country) => country.code === countryCode);
}

export function AddressAutocompleteForm(props: Props) {
  const { onValueChanged, initialValues = EMPTY_ADDRESS_FORM_STATE } = props;
  const [formState, setFormState] = useState<AddressFormState>(initialValues);

  const onRetrieve = useCallback(
    (res: SearchBoxRetrieveResponse) => {
      if (res.features.length === 0) {
        return;
      }

      const { properties } = res.features[0];
      const { context } = properties;

      const countryCode = context.country?.country_code?.toUpperCase() ?? "";
      const newState: AddressFormState = {
        address: properties.address ?? "",
        city: context.place?.name ?? "",
        postal: context.postcode?.name ?? "",
        country: isValidCountry(countryCode) ? countryCode : "",
        province: context.region?.region_code ?? "",
      };

      setFormState(newState);
      onValueChanged("address", newState.address);
      onValueChanged("city", newState.city);
      onValueChanged("postal", newState.postal);
      onValueChanged("province", newState.province);
      onValueChanged("country", newState.country);
    },
    [onValueChanged],
  );

  const onUpdateAddressValue = (
    addressType: keyof CompanyAddress,
    value: string,
  ) => {
    const newState = { ...formState, [addressType]: value };
    setFormState(newState);
    onValueChanged(addressType, value);
  };

  const regionOptionsByCountry: Record<string, typeof provinces> = {
    CA: provinces,
    US: states,
  };
  const regionOptions = regionOptionsByCountry[formState.country] ?? [];

  return (
    <Stack spacing={2}>
      <Box className="mapbox-searchbox-wrapper">
        <SearchBox
          accessToken={TOKEN}
          onRetrieve={onRetrieve}
          options={{ language: "en" }}
          theme={{
            variables: {
              fontFamily: '"Quicksand", "Helvetica", "Arial", sans-serif',
              colorPrimary: EVOTRUX_BLUE,
              borderRadius: "4px",
            },
          }}
          placeholder="Search for an address or business"
        />
      </Box>
      <TextField
        required
        label="Address"
        value={formState.address}
        onChange={(e) => onUpdateAddressValue("address", e.target.value)}
      />
      <TextField
        required
        label="City"
        value={formState.city}
        onChange={(e) => onUpdateAddressValue("city", e.target.value)}
      />
      <TextField
        required
        label="Postal"
        value={formState.postal}
        onChange={(e) => onUpdateAddressValue("postal", e.target.value)}
      />
      <Stack direction="row" spacing={2}>
        <FormControl required fullWidth>
          <InputLabel>Country</InputLabel>
          <Select
            label="Country"
            value={formState.country}
            onChange={(e) => {
              const newState = {
                ...formState,
                country: e.target.value,
                province: "",
              };
              setFormState(newState);
              onValueChanged("country", e.target.value);
              onValueChanged("province", "");
            }}
          >
            {countries.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl required fullWidth disabled={!formState.country}>
          <InputLabel>Region</InputLabel>
          <Select
            required
            label="Region"
            value={formState.province}
            onChange={(e) => onUpdateAddressValue("province", e.target.value)}
          >
            {regionOptions.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
}
